import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { calls as callsApi, shoppers as shoppersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, FilterToolbar, MetricStrip, PageHeader, SegmentedViewSwitch } from "../components/Workspace";
import type { Call, CallStatus, User } from "../types";

const ADMIN_ROLES = ["SuperAdmin", "Admin"];
const SHOPPER_ROLES = ["Mystery Shopper"];
const MIN_YEAR = 2013;

type ViewMode =
  | "all"
  | "assigned"
  | "unassigned"
  | "scheduled"
  | "reshops"
  | "completed"
  | "inprogress"
  | "overdue";

interface ShopperOption {
  id: number;
  name: string;
  email?: string;
}

function normalizeName(value: string) {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function statusTone(name: string) {
  if (name.includes("Completed")) return "bg-emerald-100 text-emerald-800";
  if (name.includes("Pending")) return "bg-amber-100 text-amber-800";
  if (name.includes("Cancelled")) return "bg-rose-100 text-rose-800";
  if (name.includes("Scheduled")) return "bg-sky-100 text-sky-800";
  if (name.includes("In progress")) return "bg-indigo-100 text-indigo-800";
  return "bg-slate-100 text-slate-700";
}

interface Props {
  user: User;
}

export default function CallsPage({ user }: Props) {
  const navigate = useNavigate();
  const roleName = user.role?.name || "";
  const isAdmin = ADMIN_ROLES.includes(roleName) || user.is_staff || user.is_superuser;
  const isShopper = SHOPPER_ROLES.includes(roleName) && !isAdmin;
  const [callList, setCallList] = useState<Call[]>([]);
  const [statuses, setStatuses] = useState<CallStatus[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const maxYear = currentYear + 1;
  const [yearFilter, setYearFilter] = useState<number | "all">(currentYear);
  const [monthFilter, setMonthFilter] = useState<number | "all">(isShopper ? currentMonth : "all");
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [selectedShoppers, setSelectedShoppers] = useState<Set<number>>(new Set());
  const [shopperSearch, setShopperSearch] = useState("");
  const [allShoppers, setAllShoppers] = useState<ShopperOption[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState<Record<ViewMode, number>>({
    all: 0,
    assigned: 0,
    unassigned: 0,
    scheduled: 0,
    reshops: 0,
    completed: 0,
    inprogress: 0,
    overdue: 0,
  });
  const [statusCounts, setStatusCounts] = useState<Record<number, number>>({});
  const [shopperCounts, setShopperCounts] = useState<Record<number, number>>({});
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const PAGE_SIZE = 50;
  const requestVersionRef = useRef(0);
  const defaultShopperSelectionAppliedRef = useRef(false);

  useEffect(() => {
    if (isAdmin) {
      shoppersApi.list("limit=500").then((res) => {
        const opts = res.items
          .filter((u: User) => u.first_name || u.last_name)
          .map((u: User) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`.trim(), email: u.email }))
          .sort((a: ShopperOption, b: ShopperOption) => a.name.localeCompare(b.name));
        const currentUserOption =
          user.first_name || user.last_name
            ? { id: user.id, name: `${user.first_name} ${user.last_name}`.trim(), email: user.email }
            : null;
        const merged = currentUserOption && !opts.some((option) => option.id === currentUserOption.id)
          ? [...opts, currentUserOption]
          : opts;
        setAllShoppers(merged.sort((a: ShopperOption, b: ShopperOption) => a.name.localeCompare(b.name)));
      });
    }
  }, [isAdmin, user.email, user.first_name, user.id, user.last_name]);

  useEffect(() => {
    if (!isAdmin || defaultShopperSelectionAppliedRef.current || selectedShoppers.size > 0 || allShoppers.length === 0) {
      return;
    }

    const fullName = normalizeName(`${user.first_name} ${user.last_name}`);
    const email = (user.email || "").trim().toLowerCase();
    const match = allShoppers.find((shopper) =>
      shopper.id === user.id ||
      ((shopper.email || "").trim().toLowerCase() !== "" && (shopper.email || "").trim().toLowerCase() === email) ||
      normalizeName(shopper.name) === fullName
    );

    if (match) {
      setSelectedShoppers(new Set([match.id]));
      defaultShopperSelectionAppliedRef.current = true;
    }
  }, [allShoppers, isAdmin, selectedShoppers.size, user.email, user.first_name, user.id, user.last_name]);

  useEffect(() => {
    callsApi.statuses().then(setStatuses);
  }, []);

  // Reset page when filters or sort change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, yearFilter, monthFilter, viewMode, sortKey, sortDir]);

  useEffect(() => {
    const summaryParams = new URLSearchParams();
    if (search) summaryParams.set("search", search);
    if (yearFilter !== "all") summaryParams.set("year", String(yearFilter));
    if (monthFilter !== "all") summaryParams.set("month", String(monthFilter));
    if (isShopper) summaryParams.set("shopper_id", String(user.id));
    if (selectedShoppers.size > 0) summaryParams.set("shopper_ids", Array.from(selectedShoppers).join(","));

    const params = new URLSearchParams(summaryParams);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String((page - 1) * PAGE_SIZE));
    if (statusFilter) params.set("status_id", statusFilter);
    params.set("sort", `${sortDir === "desc" ? "-" : ""}${sortKey}`);

    const completedStatusId = statuses.find((status) => status.name === "Completed")?.id;
    const inProgressStatusId = statuses.find((status) => status.name === "In progress")?.id;

    switch (viewMode) {
      case "assigned":
        params.set("has_shopper", "true");
        break;
      case "unassigned":
        params.set("has_shopper", "false");
        break;
      case "scheduled":
        params.set("has_scheduled", "true");
        break;
      case "reshops":
        params.set("is_reshop_call", "true");
        break;
      case "completed":
        if (completedStatusId) params.set("status_id", String(completedStatusId));
        break;
      case "inprogress":
        if (inProgressStatusId) params.set("status_id", String(inProgressStatusId));
        break;
      case "overdue":
        params.set("is_overdue", "true");
        break;
      default:
        break;
    }

    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    Promise.allSettled([
      callsApi.list(params.toString()),
      callsApi.summary(summaryParams.toString()),
    ]).then(([pageResult, summaryResult]) => {
      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      if (pageResult.status === "fulfilled") {
        setCallList(pageResult.value.items);
        setTotalCount(pageResult.value.count);
      } else {
        setCallList([]);
        setTotalCount(0);
      }

      if (summaryResult.status === "fulfilled") {
        setSummaryCounts({
          all: summaryResult.value.total_matches,
          assigned: summaryResult.value.assigned,
          unassigned: summaryResult.value.unassigned,
          scheduled: summaryResult.value.scheduled,
          reshops: summaryResult.value.reshops,
          completed: summaryResult.value.completed,
          inprogress: summaryResult.value.inprogress,
          overdue: summaryResult.value.overdue,
        });

        const nextStatusCounts: Record<number, number> = {};
        summaryResult.value.status_counts.forEach((status) => {
          nextStatusCounts[status.status_id] = status.count;
        });
        setStatusCounts(nextStatusCounts);

        const nextShopperCounts: Record<number, number> = {};
        summaryResult.value.shopper_counts.forEach((shopper) => {
          nextShopperCounts[shopper.shopper_id] = shopper.count;
        });
        setShopperCounts(nextShopperCounts);
      } else {
        setSummaryCounts({
          all: 0,
          assigned: 0,
          unassigned: 0,
          scheduled: 0,
          reshops: 0,
          completed: 0,
          inprogress: 0,
          overdue: 0,
        });
        setStatusCounts({});
        setShopperCounts({});
      }
    });
  }, [search, statusFilter, yearFilter, monthFilter, viewMode, isShopper, user.id, page, sortKey, sortDir, selectedShoppers, statuses]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortHeader = ({ label, sortField, align }: { label: string; sortField: string; align?: string }) => (
    <th
      className={`cursor-pointer select-none hover:text-slate-900 ${align || ""}`}
      onClick={() => toggleSort(sortField)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === sortField ? (
          <span className="text-indigo-600">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>
        ) : (
          <span className="text-slate-300">{"\u25B4"}</span>
        )}
      </span>
    </th>
  );

  const toggleShopper = (id: number) => {
    setSelectedShoppers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredShopperOptions = allShoppers.filter(
    (shopper) => !shopperSearch || shopper.name.toLowerCase().includes(shopperSearch.toLowerCase())
  );

  const selectAllVisible = () => {
    const visible = filteredShopperOptions.map((shopper) => shopper.id);
    setSelectedShoppers((prev) => {
      const next = new Set(prev);
      visible.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearShopperSelection = () => setSelectedShoppers(new Set());

  const availableYears = Array.from({ length: maxYear - MIN_YEAR + 1 }, (_, index) => MIN_YEAR + index).reverse();
  const monthOptions = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const visibleCalls = callList;

  const summaryItems = [
    { key: "all" as ViewMode, label: "All Calls", count: summaryCounts.all },
    { key: "assigned" as ViewMode, label: "Assigned", count: summaryCounts.assigned },
    { key: "unassigned" as ViewMode, label: "Unassigned", count: summaryCounts.unassigned },
    { key: "scheduled" as ViewMode, label: "Scheduled", count: summaryCounts.scheduled },
    { key: "reshops" as ViewMode, label: "Reshop Calls", count: summaryCounts.reshops },
    { key: "completed" as ViewMode, label: "Completed", count: summaryCounts.completed },
    { key: "inprogress" as ViewMode, label: "In Progress", count: summaryCounts.inprogress },
    { key: "overdue" as ViewMode, label: "Overdue", count: summaryCounts.overdue },
  ];

  const pageTitle = isAdmin ? "Manage Calls" : isShopper ? "My Calls" : "Property Calls";
  const pageHint = isAdmin
    ? "Live queue, survey progress, and call detail management."
    : isShopper
    ? "View calls assigned to you, open details, and complete surveys."
    : "View calls related to your properties.";

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const metricItems = [
    { label: "Total matches", value: totalCount, detail: "Calls after current filters", tone: "brand" as const },
    { label: "Completed", value: summaryCounts.completed, detail: "Completed after current filters", tone: "success" as const },
    { label: "Overdue", value: summaryCounts.overdue, detail: "Needs follow-up now", tone: "danger" as const },
    { label: "Unassigned", value: summaryCounts.unassigned, detail: "Coverage still needed", tone: "warning" as const },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={pageTitle}
        title="Calls Desk"
        description={pageHint}
        compact
        badges={[
          { label: `Year ${yearFilter === "all" ? "All" : yearFilter}`, tone: "neutral" },
          { label: `Month ${monthFilter === "all" ? "All" : monthOptions.find((m) => m.value === monthFilter)?.label || monthFilter}`, tone: "neutral" },
          ...(selectedShoppers.size > 0
            ? [{ label: `${selectedShoppers.size} shoppers selected`, tone: "brand" as const }]
            : []),
        ]}
      />

      <MetricStrip items={metricItems} />

      <FilterToolbar sticky className="rise-in delay-1">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.35fr),12rem,12rem,9rem,9rem,9rem]">
          <input
            placeholder="Search by legacy/new call ID, shop ID, property, hotel code, or shopper..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-shell"
          />
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
            className="select-shell"
          >
            {summaryItems.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select-shell"
          >
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status.id} value={String(status.id)}>
                {status.name}
              </option>
            ))}
          </select>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="select-shell"
          >
            <option value="all">All years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="select-shell"
          >
            <option value="all">All months</option>
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              setYearFilter(currentYear);
              setMonthFilter(isShopper ? currentMonth : "all");
              setViewMode("all");
              setSelectedShoppers(new Set());
              setShopperSearch("");
            }}
            className="button-ghost"
          >
            Clear
          </button>
        </div>

        <SegmentedViewSwitch
          items={summaryItems.map((item) => ({ key: item.key, label: item.label, count: item.count }))}
          activeKey={viewMode}
          onChange={(key) => setViewMode(key as ViewMode)}
          className="mt-3"
        />

        {statuses.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStatusFilter("")}
              className={`segmented-item ${statusFilter === "" ? "is-active" : ""}`.trim()}
            >
              <span>All statuses</span>
              <span className="segmented-count">{totalCount}</span>
            </button>
            {statuses.map((status) => {
              const count = statusCounts[status.id] || 0;
              const active = statusFilter === String(status.id);
              return (
                <button
                  key={status.id}
                  type="button"
                  onClick={() => setStatusFilter(String(status.id))}
                  className={`segmented-item ${active ? "is-active" : ""}`.trim()}
                >
                  <span>{status.name}</span>
                  <span className="segmented-count">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {isAdmin && (
          <div className="filter-subsection">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">Shopper scope</div>
                <div className="mt-1 text-xs text-slate-500">
                  Keep shopper filtering inside the queue workspace.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  placeholder="Search shoppers..."
                  value={shopperSearch}
                  onChange={(e) => setShopperSearch(e.target.value)}
                  className="input-shell min-w-[16rem] !py-2 text-sm"
                />
                <button type="button" onClick={selectAllVisible} className="button-ghost !px-4 !py-2 !text-sm">
                  Select visible
                </button>
                <button type="button" onClick={clearShopperSelection} className="button-ghost !px-4 !py-2 !text-sm">
                  Clear shoppers
                </button>
                <div className="badge-pill tone-neutral">
                  {filteredShopperOptions.length} shoppers
                </div>
              </div>
            </div>

            <div className="mt-3 flex max-h-40 flex-wrap gap-2 overflow-y-auto pr-1">
              {filteredShopperOptions.map((shopper) => {
                const checked = selectedShoppers.has(shopper.id);
                const count = shopperCounts[shopper.id] || 0;
                return (
                  <button
                    key={shopper.id}
                    type="button"
                    onClick={() => toggleShopper(shopper.id)}
                    className={`segmented-item ${checked ? "is-active" : ""}`.trim()}
                  >
                    <span>{shopper.name}</span>
                    <span className="segmented-count">{count}</span>
                  </button>
                );
              })}
              {filteredShopperOptions.length === 0 && (
                <div className="px-1 py-2 text-sm text-slate-500">No shoppers match the current search.</div>
              )}
            </div>
          </div>
        )}

        {yearFilter !== "all" && (yearFilter < MIN_YEAR || yearFilter > maxYear) && (
          <div className="mt-3 text-sm text-rose-700">
            Year must be between {MIN_YEAR} and {maxYear}.
          </div>
        )}
      </FilterToolbar>

      <DataTableShell
        title="Live queue"
        subtitle="The table stays dominant so operators can move from filters into row-level action without losing context."
        className="rise-in delay-2"
      >
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <SortHeader label="ID" sortField="id" />
                <SortHeader label="Property" sortField="property" />
                {isAdmin && <SortHeader label="HMC" sortField="hmc" />}
                <th>Salesperson</th>
                {!isShopper && <SortHeader label="Shopper" sortField="shopper" />}
                <SortHeader label="Type" sortField="type" />
                <SortHeader label="Service Level" sortField="service_level" />
                <SortHeader label="Status" sortField="status" />
                <SortHeader label="Scheduled" sortField="scheduled" />
                <SortHeader label="Score" sortField="score" align="text-right" />
                {isAdmin && <th className="text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {visibleCalls.map((call) => (
                <tr
                  key={call.id}
                  className="table-row-button cursor-pointer"
                  onClick={() => navigate(`/calls/${call.id}`)}
                >
                  <td>
                    <div className="font-mono text-sm font-semibold text-slate-900">
                      #{call.legacy_id || call.id}
                    </div>
                    {call.legacy_id && call.legacy_id !== call.id && (
                      <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        New #{call.id}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="font-semibold text-slate-900">{call.property_name}</div>
                    <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                      {call.property_hotel_code}
                    </div>
                  </td>
                  {isAdmin && <td className="text-sm text-slate-600">{call.management_company_name || "-"}</td>}
                  <td>
                    <div className="font-medium text-slate-900">{call.sales_staff_name || "Unassigned"}</div>
                  </td>
                  {!isShopper && (
                    <td>
                      <div className="font-medium text-slate-900">{call.shopper_name || "Unassigned"}</div>
                    </td>
                  )}
                  <td>{call.call_type.name}</td>
                  <td>{call.service_level.name}</td>
                  <td>
                    <span className={`badge-pill ${statusTone(call.status.name)}`}>{call.status.name}</span>
                  </td>
                  <td className="text-sm text-slate-600">{call.scheduled_call_date || "-"}</td>
                  <td className="text-right">
                    {call.percent_achieved != null ? (
                      <span className={`badge-pill ${
                        call.percent_achieved >= 70 ? "bg-emerald-100 text-emerald-800" :
                        call.percent_achieved >= 50 ? "bg-amber-100 text-amber-800" :
                        "bg-rose-100 text-rose-800"
                      }`}>
                        {call.percent_achieved}%
                      </span>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="w-[7rem]">
                      <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span
                          title={call.is_survey_completed ? "Survey completed" : "Survey not completed"}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-[13px] transition ${
                            call.is_survey_completed
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-50 text-slate-300"
                          }`}
                        >
                          <span aria-hidden="true" className="leading-none">{call.is_survey_completed ? "✓" : "○"}</span>
                        </span>
                        <span
                          title={call.survey_email_sent ? "Review email sent" : "Review email not sent"}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-[12px] transition ${
                            call.survey_email_sent
                              ? "border-sky-200 bg-sky-50 text-sky-700"
                              : "border-slate-200 bg-slate-50 text-slate-300"
                          }`}
                        >
                          <span aria-hidden="true" className="leading-none">✉</span>
                        </span>
                        <span
                          title={call.audio_url || call.wave_file ? "Audio available" : "No audio"}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-[13px] transition ${
                            call.audio_url || call.wave_file
                              ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-slate-50 text-slate-300"
                          }`}
                        >
                          <span aria-hidden="true" className="leading-none">♫</span>
                        </span>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {visibleCalls.length === 0 && (
          <EmptyState title="No calls found" message="Adjust the queue view, search, shopper, or status filters." />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 mt-2">
            <p className="text-sm text-slate-600">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="button-ghost text-xs px-2 py-1 disabled:opacity-40"
              >
                First
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="button-ghost text-xs px-2 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <span className="text-sm font-medium text-slate-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="button-ghost text-xs px-2 py-1 disabled:opacity-40"
              >
                Next
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="button-ghost text-xs px-2 py-1 disabled:opacity-40"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </DataTableShell>
    </div>
  );
}
